import { useEffect } from 'react';
import * as uuid from 'uuid';
import { useDispatch } from 'react-redux';
import { Elements, Edge, Node } from 'react-flow-renderer';
import {
	NODE_TYPES,
	NodeConditionParams,
	ReactFlowSchema,
	ReactFlowDecisionTree,
	RehabAction,
	isCustomCostAction,
} from '@Types';
import {
	setNodeCondition,
	setCurrentTreeId,
	getRehabTree,
	addRehabTreeValidationResult,
	resetFilters,
	addNewRehabActionToTree,
	updateRehabActionInTree,
} from '@Actions';
import { restoreTree } from '@Utils/TreeUtils';
import { buildNodeData, NodeHandlers } from './buildNodeData';

export interface UseTreeSyncArgs {
	treeId: string;
	currentTree: ReactFlowDecisionTree | null;
	edgeConnectorObject: NodeConditionParams;
	rehabDeletedActions: string[];
	shouldEnableMultipleInputs: boolean | string | Object;
	elements: Elements;
	setElements: React.Dispatch<React.SetStateAction<Elements>>;
	setIsDirty: (dirty: boolean) => void;
	setCurrentTreeName: (name: string) => void;
	nodeHandlers: NodeHandlers;
	isRehabTreeEditable: boolean;
	addToTree: (values: ReactFlowSchema) => void;
	updateInTree: (valuesArray: ReactFlowSchema[]) => void;
	addNewActionToTree: RehabAction | undefined;
	updateActionInTree: RehabAction | undefined;
	defectCodes: string[] | null;
}

export const useTreeSync = ({
	treeId,
	currentTree,
	edgeConnectorObject,
	rehabDeletedActions,
	shouldEnableMultipleInputs,
	elements,
	setElements,
	setIsDirty,
	setCurrentTreeName,
	nodeHandlers,
	isRehabTreeEditable,
	addToTree,
	updateInTree,
	addNewActionToTree: addNewActionToTreeSelector,
	updateActionInTree: updateActionInTreeSelector,
	defectCodes,
}: UseTreeSyncArgs): void => {
	const dispatch = useDispatch();

	// Init effect
	useEffect(() => {
		dispatch(setCurrentTreeId(treeId));
		if (treeId !== undefined && treeId != '') {
			dispatch(
				getRehabTree({
					Id: treeId,
				}),
			);
			dispatch(resetFilters());
			dispatch(
				addRehabTreeValidationResult({
					treeId: treeId,
					isValid: true,
					validationMessages: [],
				}),
			);
		}
	}, []);

	// Add new action to tree effect
	useEffect(() => {
		if (addNewActionToTreeSelector) {
			const node = elements.find(
				node =>
					node.data?.nodeData?.actionId ==
					addNewActionToTreeSelector._id,
			);

			if (!node) {
				const reactFlowItem: ReactFlowSchema = {
					id: null,
					parentId: null,
					type: NODE_TYPES.action,
					label: addNewActionToTreeSelector.actionId,
					data: {
						label: addNewActionToTreeSelector.actionId,
						nodeData: {
							title: addNewActionToTreeSelector.actionId,
							actionDefectCodes: defectCodes ?? [],
							actionId: addNewActionToTreeSelector._id,
							treeId: addNewActionToTreeSelector.treeId,
							unit: isCustomCostAction(addNewActionToTreeSelector)
								? addNewActionToTreeSelector.unit
								: undefined,
							unitCost: isCustomCostAction(
								addNewActionToTreeSelector,
							)
								? addNewActionToTreeSelector.unitCost
								: undefined,
							notes: addNewActionToTreeSelector.notes,
						},
					},
				};

				addToTree(reactFlowItem);
				setIsDirty(true);
			}
			dispatch(addNewRehabActionToTree(undefined));
		}
	}, [addNewActionToTreeSelector]);

	// Update action in tree effect
	useEffect(() => {
		if (updateActionInTreeSelector) {
			const reactFlowItems: ReactFlowSchema[] = [];
			elements.forEach(node => {
				if (
					node.data?.nodeData?.actionId ==
					updateActionInTreeSelector._id
				) {
					const reactFlowItem: ReactFlowSchema = {
						id: node ? node.id : uuid.v4(),
						parentId: null,
						type: NODE_TYPES.action,
						label: updateActionInTreeSelector.actionId,
						data: {
							label: updateActionInTreeSelector.actionId,
							nodeData: {
								title: updateActionInTreeSelector.actionId,
								actionDefectCodes: defectCodes ?? [],
								actionId: updateActionInTreeSelector._id,
								treeId: updateActionInTreeSelector.treeId,
								unit: isCustomCostAction(
									updateActionInTreeSelector,
								)
									? updateActionInTreeSelector.unit
									: undefined,
								unitCost: isCustomCostAction(
									updateActionInTreeSelector,
								)
									? updateActionInTreeSelector.unitCost
									: undefined,
								notes: updateActionInTreeSelector.notes,
							},
						},
					};

					reactFlowItems.push(reactFlowItem);
				}
			});
			if (reactFlowItems.length > 0) {
				updateInTree(reactFlowItems);
			}
			dispatch(updateRehabActionInTree(undefined));
		}
	}, [updateActionInTreeSelector]);

	// Edge connector dirty effect
	useEffect(() => {
		if (edgeConnectorObject.id && edgeConnectorObject.delete)
			setIsDirty(true);
	}, [edgeConnectorObject]);

	// Edge connector update/delete effect
	useEffect(() => {
		if (edgeConnectorObject.id) {
			setElements((els: Elements) => {
				const edgeToUpdate = els?.find(
					el => el.id === edgeConnectorObject.id,
				);
				if (edgeToUpdate) {
					if (edgeConnectorObject.delete) {
						setIsDirty(true);
						return els?.filter(
							edge => edge.id != edgeConnectorObject.id,
						);
					} else {
						return els?.map(el => {
							if (el.id == edgeConnectorObject.id) {
								return {
									...el,
									label: edgeConnectorObject.connectorCondition
										? 'True'
										: 'False',
								};
							}

							return el;
						});
					}
				} else {
					return els;
				}
			});

			const params: NodeConditionParams = {
				id: '',
			};
			dispatch(setNodeCondition(params));
		}
	}, [edgeConnectorObject]);

	// Rehab deleted actions effect
	useEffect(() => {
		if (rehabDeletedActions && rehabDeletedActions.length !== 0) {
			setIsDirty(true);
		}
	}, [rehabDeletedActions]);

	// currentTree → elements mapping effect
	useEffect(() => {
		if (currentTree?._id === treeId && currentTree?.nodes) {
			setCurrentTreeName(currentTree.name);
			let updatedNodes: (Node | Edge)[] = [];
			updatedNodes = currentTree.nodes?.map((node: ReactFlowSchema) => {
				let updatedNode!: Node | Edge;
				if (node.id) {
					if (node.target && node.source) {
						// edge
						updatedNode = {
							id: node.id.toString(),
							label: node.label,
							source: node.source.toString(),
							target: node.target.toString(),
							type: 'buttonedge',
						};
					} else {
						// node
						updatedNode = {
							id: node.id.toString(),
							type: node.type ?? NODE_TYPES.query,
							style: node.style ?? {},
							data: buildNodeData(
								node.id,
								node.data?.label,
								node.data?.nodeData,
								nodeHandlers,
								isRehabTreeEditable,
							),
							position: node.position ?? { x: 24, y: 24 },
						};
					}
				}
				return updatedNode;
			});
			if (shouldEnableMultipleInputs) {
				const restoredElements = restoreTree(updatedNodes);
				setElements(restoredElements);
			} else {
				setElements(updatedNodes);
			}
		}
	}, [currentTree]);
};
